/**
 * The client portal's modules, and what a single contact is allowed to open.
 *
 * One list, three readers, and that is the whole reason this file is in
 * `@wa/core` rather than beside any one of them:
 *   - the API gates every `/api/portal/*` read on it (`modules/portal/lib/scope.ts`);
 *   - the CRM draws one toggle per entry on the contact row;
 *   - the portal shell shows one tab per entry the server allowed.
 * Three copies of a list like this drift in one predictable direction: a module
 * gets added to the tab strip and to the toggles, and the gate keeps answering
 * for the four it knew about. The tab appears, the data behind it does not.
 *
 * The keys are the portal's own vocabulary, not the CRM's table names.
 * `support` covers tickets and `invoices` covers invoices only, because those
 * are the two the client recognises; estimates are their own entry for the same
 * reason Perfex kept them separate - a company may be shown what it owes without
 * being shown what it was quoted.
 *
 * `statement` is the account statement: every invoice and every payment on one
 * running balance. It is its OWN module rather than a panel under `invoices`,
 * even though it reads the same rows, because the two answer different questions
 * to different people. The invoice list is "what do I owe on this document"; the
 * statement is "what has passed between our two companies", which is a
 * bookkeeper's page and the one an operator is most likely to want to withhold
 * from a client who only ever pays a single retainer.
 */

/** Every module a portal contact can be granted. Order is the tab order. */
export const PORTAL_MODULES = [
  "invoices",
  "estimates",
  "statement",
  "projects",
  "support",
  "contracts",
] as const;

export type PortalModule = (typeof PORTAL_MODULES)[number];

const MODULE_SET: ReadonlySet<string> = new Set<string>(PORTAL_MODULES);

export function isPortalModule(value: unknown): value is PortalModule {
  return typeof value === "string" && MODULE_SET.has(value);
}

/**
 * What one contact may open, as stored on `Contact.portalModules`.
 *
 * `null` is NOT "nothing". It is "never restricted", which is what every row
 * written before this feature existed means and what a freshly invited contact
 * gets. An empty array is the opposite and equally real answer: the operator
 * granted the portal and then took every module away, which leaves a person who
 * can sign in and read their own profile and nothing else.
 *
 * Collapsing the two is the reason this is a nullable Json column rather than a
 * Prisma `String[]`: a scalar list has no null, so an unset row and a stripped
 * row would arrive here as the same `[]` and every contact in production would
 * silently lose the portal on the day the column shipped.
 */
export type PortalModuleGrant = PortalModule[] | null;

/**
 * Read a stored grant back into a value the gate can use.
 *
 * Anything that is not an array is `null`, unrestricted: a column holding
 * garbage must fail towards the behaviour every row had before the column
 * existed, not towards locking a paying client out of their invoices. Unknown
 * strings inside the array are dropped rather than kept, because a key we do
 * not recognise cannot be checked against anything.
 */
export function parsePortalModules(raw: unknown): PortalModuleGrant {
  if (!Array.isArray(raw)) return null;
  const seen = new Set<PortalModule>();
  for (const v of raw) if (isPortalModule(v)) seen.add(v);
  return PORTAL_MODULES.filter((m) => seen.has(m));
}

/** Does this grant open that module? A `null` grant opens all of them. */
export function grantAllows(grant: PortalModuleGrant, module: PortalModule): boolean {
  return grant === null || grant.includes(module);
}

/** The modules a grant opens, spelled out. Useful where `null` has to be shown. */
export function grantedModules(grant: PortalModuleGrant): PortalModule[] {
  return grant === null ? [...PORTAL_MODULES] : grant;
}

/**
 * The union of several grants, in tab order.
 *
 * One person can be a contact at more than one company, so the portal shell has
 * to answer "is there ANY company whose invoices you may read" before it can
 * decide whether to draw the Invoices tab. The union is the right answer to that
 * question and the wrong answer to every other one: it says a tab is worth
 * drawing, never that a particular company's records are readable. That second
 * question is per client and is answered by the gate, not here.
 */
export function unionGrants(grants: readonly PortalModuleGrant[]): PortalModule[] {
  if (grants.length === 0) return [];
  if (grants.some((g) => g === null)) return [...PORTAL_MODULES];
  const seen = new Set<PortalModule>();
  for (const g of grants) for (const m of g!) seen.add(m);
  return PORTAL_MODULES.filter((m) => seen.has(m));
}

/**
 * Two grants of the SAME vocabulary, narrowed against each other.
 *
 * `null` on either side is "no opinion at this level", so it yields to the
 * other. Two opinions produce their intersection, in vocabulary order.
 *
 * This is the OPPOSITE operation from `unionGrants` and the two are never
 * interchangeable. The union answers "is this tab worth drawing anywhere at
 * all"; the intersection answers "may this person open this door at this
 * company". Reaching for the wrong one is not a style slip: a union where an
 * intersection belongs hands out access nobody granted.
 */
export function intersectGrants(a: PortalModuleGrant, b: PortalModuleGrant): PortalModuleGrant {
  if (a === null) return b;
  if (b === null) return a;
  const bs = new Set(b);
  return PORTAL_MODULES.filter((m) => a.includes(m) && bs.has(m));
}

/**
 * The three levels a module grant is decided at, named.
 *
 * Every field is required. A level nobody supplied would default to `null`,
 * which reads as "unrestricted" - so an omission would widen access silently,
 * with a 200 and nothing in any log. Making the caller name all three turns that
 * omission into a build failure.
 */
export interface ModuleGrantLevels {
  /** The CRM-wide default (the `crm.portal` Setting). `null` = no rule at all. */
  global: PortalModuleGrant;
  /** `Client.portalModules`. `null` = this company did not decide, inherit. */
  client: PortalModuleGrant;
  /** `Contact.portalModules`. `null` = this person's row narrows nothing. */
  contact: PortalModuleGrant;
}

/**
 * What this company may open, before any per-person narrowing.
 *
 * REPLACE, not intersect. A client row beats the default outright, INCLUDING
 * upwards: a default of ["invoices"] plus a client row of ["invoices","support"]
 * opens support for that one company. If the override could only subtract, then
 * selling one client a module we do not give away by default would mean widening
 * the default for everybody, and the per-client control would be useless for the
 * thing operators actually want it for.
 */
export function resolveCompanyGrant(
  global: PortalModuleGrant,
  client: PortalModuleGrant,
): PortalModuleGrant {
  return client ?? global;
}

/**
 * THE precedence rule, and the only place it exists.
 *
 * The company's grant (see above) INTERSECTED with the contact's own row.
 * Narrowing only, and that asymmetry is deliberate: a contact grant that could
 * widen would let an operator hand one employee a module the company was never
 * sold, out of a row that reads on screen as a restriction. It is also what
 * makes `Client.portalModules = []` a real kill switch rather than a control
 * that quietly loses to every contact holding an explicit list.
 *
 * All three null returns null, which is exactly what every row in the database
 * meant before this function existed. That is the shipping guarantee: on the day
 * the columns land, nothing anywhere changes.
 *
 * NEVER call `grantAllows` on a single level. Call it on this function's result.
 * `modules/portal/lib/grantReaders.test.ts` in the API enforces that by walking
 * the source, because a reader that resolves one level reads a restricted client
 * as unrestricted and nothing about that failure is visible.
 */
export function resolveModuleGrant(levels: ModuleGrantLevels): PortalModuleGrant {
  return intersectGrants(resolveCompanyGrant(levels.global, levels.client), levels.contact);
}
